import { useCallback, useState } from 'react';

import { LDFlags } from '../constants/flags';
import {
  EmployerPhoneLookupForm,
  EmployerPhoneLookupSource,
  useEmployerPhoneLookupMutation,
} from '../gql/generated/graphql';
import { isSelfEmployed, shouldSkipEmployerLookup } from '../utils/employerPhoneLookup';
import useFlag from './useFlag';

type LookupArgs = {
  employerName: string;
  city: string;
  state: string;
  zip: string;
  userFirstName: string;
  userLastName: string;
  userPhone: string;
  temporaryInfoId: string;
  form: EmployerPhoneLookupForm;
};

export type EmployerPhoneLookupResult = {
  phoneNumber: string | null;
  isSelfEmployed: boolean;
};

export type UseEmployerPhoneLookupResult = {
  phoneNumber: string | null;
  loading: boolean;
  error: boolean;
  source: EmployerPhoneLookupSource | null;
  isSelfEmployedResult: boolean;
  lookup: (args: LookupArgs) => Promise<EmployerPhoneLookupResult>;
  reset: () => void;
};

const useEmployerPhoneLookup = (): UseEmployerPhoneLookupResult => {
  const flagOn = useFlag(LDFlags.AUTO_POPULATE_EMPLOYER_PHONE);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [source, setSource] = useState<EmployerPhoneLookupSource | null>(null);
  const [isSelfEmployedResult, setIsSelfEmployedResult] = useState(false);

  const [employerPhoneLookupMutation] = useEmployerPhoneLookupMutation({
    context: { isErrorHandled: true },
  });

  const reset = useCallback(() => {
    setPhoneNumber(null);
    setLoading(false);
    setError(false);
    setSource(null);
    setIsSelfEmployedResult(false);
  }, []);

  const lookup = useCallback<UseEmployerPhoneLookupResult['lookup']>(
    async (args) => {
      if (!flagOn) return { phoneNumber: null, isSelfEmployed: false };

      if (shouldSkipEmployerLookup(args.employerName)) {
        reset();
        setSource(EmployerPhoneLookupSource.NotFound);
        return { phoneNumber: null, isSelfEmployed: false };
      }

      reset();
      setLoading(true);

      const input = {
        employerName: args.employerName,
        city: args.city,
        state: args.state,
        zip: args.zip,
        temporaryInfoId: args.temporaryInfoId,
        form: args.form,
      };

      if (isSelfEmployed(args.employerName, args.userFirstName, args.userLastName)) {
        const selfPhone = args.userPhone || null;
        // No lookup needed: the user's own phone fills instantly. The call is
        // never awaited; with selfEmployed the backend skips the phone search
        // and only records the attempt for analytics.
        employerPhoneLookupMutation({
          variables: { input: { ...input, selfEmployed: true, userPhone: selfPhone } },
        }).catch(() => undefined);

        setIsSelfEmployedResult(true);
        setSource(EmployerPhoneLookupSource.SelfEmployed);
        setPhoneNumber(selfPhone);
        setLoading(false);
        return { phoneNumber: selfPhone, isSelfEmployed: true };
      }

      try {
        const { data } = await employerPhoneLookupMutation({ variables: { input } });

        const result = data?.employerPhoneLookup ?? null;
        const phone = result?.phoneNumber ?? null;
        setPhoneNumber(phone);
        setSource(result?.source ?? EmployerPhoneLookupSource.NotFound);
        setLoading(false);
        return { phoneNumber: phone, isSelfEmployed: false };
      } catch {
        // No user-visible error: lookup is best-effort.
        setError(true);
        setSource(EmployerPhoneLookupSource.NotFound);
        setLoading(false);
        return { phoneNumber: null, isSelfEmployed: false };
      }
    },
    [flagOn, employerPhoneLookupMutation, reset],
  );

  return {
    phoneNumber,
    loading,
    error,
    source,
    isSelfEmployedResult,
    lookup,
    reset,
  };
};

export default useEmployerPhoneLookup;
